import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, KpiCard, ProgressBar, cn, formatCurrency } from '@careandkey/shared';
import { supabase } from '../lib/supabase';
import { Layout, Topbar } from '../components/Layout';

export function SalesDashboard() {
  const { t, i18n } = useTranslation();
  const [d, setD] = useState<Record<string, any[]>>({});

  useEffect(() => {
    (async () => {
      const names = ['dash_pipeline', 'dash_conversion_by_source', 'dash_mrr', 'dash_churn', 'dash_revenue_by_service_line', 'dash_revenue_by_property_type', 'dash_aged_receivables', 'dash_commissions_due'];
      const res = await Promise.all(names.map((n) => supabase.from(n).select('*')));
      setD(Object.fromEntries(names.map((n, i) => [n, (res[i]!.data as any[]) ?? []])));
    })();
  }, []);

  const mrr = (d.dash_mrr ?? []);
  const churn = (d.dash_churn ?? [])[0];
  const churnRate = churn && churn.active + churn.lost_this_month > 0 ? Math.round((100 * churn.lost_this_month) / (churn.active + churn.lost_this_month)) : 0;
  const pipeline = [...(d.dash_pipeline ?? [])].sort((a, b) => a.position - b.position);
  const pipelineMax = Math.max(1, ...pipeline.map((p) => Number(p.value || 0)));
  const slName = (r: any) => r.name_i18n?.[i18n.language] ?? r.name_i18n?.en ?? r.service_line_code;
  const cur = mrr[0]?.currency ?? d.dash_aged_receivables?.[0]?.currency ?? 'IDR';

  return (
    <Layout header={<Topbar />}>
      <div className="flex flex-col gap-[18px] px-7 py-6">
        <h1 className="text-[30px] font-semibold tracking-[-0.02em]">{t('nav.salesDash')}</h1>

        <section className="grid grid-cols-1 gap-3.5 sm:grid-cols-2 lg:grid-cols-4">
          <KpiCard label={t('sd.mrr')} value={formatCurrency(mrr.reduce((s, m) => s + Number(m.mrr || 0), 0), cur, i18n.language)} dark />
          <KpiCard label={t('sd.activeContracts')} value={churn?.active ?? 0} />
          <KpiCard label={t('sd.churn')} value={`${churnRate}%`} sub={`${churn?.lost_this_month ?? 0} ${t('sd.lostThisMonth')}`} />
          <KpiCard label={t('sd.commissionsDue')} value={formatCurrency((d.dash_commissions_due ?? []).reduce((s, c) => s + Number(c.commission_due || 0), 0), cur, i18n.language)} />
        </section>

        <div className="flex flex-wrap items-start gap-[18px]">
          <Card className="min-w-0 flex-[1_1_360px] p-5">
            <h2 className="mb-3 text-[15px] font-semibold">{t('sd.pipeline')}</h2>
            {pipeline.length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
            {pipeline.map((p) => (
              <div key={p.stage_id} className="mb-2 flex flex-col gap-1">
                <div className="flex items-center justify-between text-sm">
                  <span className={cn(p.is_won && 'text-st-done-fg', p.is_lost && 'text-st-overdue-fg')}>{p.stage_name} <span className="font-mono text-xs text-muted">{p.leads}</span></span>
                  <span className="font-mono text-xs">{formatCurrency(p.value, p.currency, i18n.language)}</span>
                </div>
                <ProgressBar value={(Number(p.value || 0) / pipelineMax) * 100} />
              </div>
            ))}
          </Card>

          <Card className="min-w-0 flex-[1_1_360px] p-5">
            <h2 className="mb-3 text-[15px] font-semibold">{t('sd.conversionBySource')}</h2>
            {(d.dash_conversion_by_source ?? []).length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
            {(d.dash_conversion_by_source ?? []).map((r, i) => (
              <div key={i} className="flex items-center justify-between border-b border-divider py-2 text-sm last:border-0">
                <span>{r.source}</span>
                <span className="font-mono text-xs text-muted">{r.converted}/{r.leads} · <span className={cn((r.conversion_pct ?? 0) >= 30 ? 'text-st-done-fg' : 'text-fg')}>{r.conversion_pct ?? 0}%</span></span>
              </div>
            ))}
          </Card>
        </div>

        <div className="flex flex-wrap items-start gap-[18px]">
          <Card className="min-w-0 flex-[1_1_300px] p-5">
            <h2 className="mb-3 text-[15px] font-semibold">{t('sd.revByServiceLine')}</h2>
            {(d.dash_revenue_by_service_line ?? []).length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
            {(d.dash_revenue_by_service_line ?? []).map((r, i) => (
              <div key={i} className="flex items-center justify-between border-b border-divider py-2 text-sm last:border-0"><span>{slName(r)}</span><span className="font-mono">{formatCurrency(r.revenue, r.currency, i18n.language)}</span></div>
            ))}
          </Card>
          <Card className="min-w-0 flex-[1_1_300px] p-5">
            <h2 className="mb-3 text-[15px] font-semibold">{t('sd.revByPropertyType')}</h2>
            {(d.dash_revenue_by_property_type ?? []).length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
            {(d.dash_revenue_by_property_type ?? []).map((r, i) => (
              <div key={i} className="flex items-center justify-between border-b border-divider py-2 text-sm last:border-0"><span>{t('propertyType.' + r.property_type)}</span><span className="font-mono">{formatCurrency(r.revenue, r.currency, i18n.language)}</span></div>
            ))}
          </Card>
          <Card className="min-w-0 flex-[1_1_300px] p-5">
            <h2 className="mb-3 text-[15px] font-semibold">{t('sd.agedReceivables')}</h2>
            {(d.dash_aged_receivables ?? []).length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
            {(d.dash_aged_receivables ?? []).map((r, i) => (
              <div key={i} className="flex flex-col gap-1 text-sm">
                {[['0–30', r.d0_30], ['31–60', r.d31_60], ['61–90', r.d61_90], ['90+', r.d90_plus]].map(([k, v]) => (
                  <div key={k as string} className="flex items-center justify-between"><span className="text-muted">{k as string} {t('sd.days')}</span><span className="font-mono">{formatCurrency(Number(v), r.currency, i18n.language)}</span></div>
                ))}
                <div className="mt-1 flex items-center justify-between border-t border-divider pt-1 font-semibold"><span>{t('sd.outstanding')}</span><span className="font-mono">{formatCurrency(r.total_outstanding, r.currency, i18n.language)}</span></div>
              </div>
            ))}
          </Card>
        </div>

        <Card className="p-5">
          <h2 className="mb-3 text-[15px] font-semibold">{t('sd.commissionsDue')}</h2>
          {(d.dash_commissions_due ?? []).length === 0 && <span className="text-sm text-muted">{t('common.noData')}</span>}
          {(d.dash_commissions_due ?? []).map((r) => (
            <div key={r.referrer_id} className="flex items-center justify-between border-b border-divider py-2 text-sm last:border-0">
              <span>{r.referrer_name} <span className="font-mono text-xs text-muted">· {r.contracts} {t('nav.contracts')}</span></span>
              <span className="font-mono">{formatCurrency(r.commission_due, r.currency ?? cur, i18n.language)}</span>
            </div>
          ))}
        </Card>
      </div>
    </Layout>
  );
}
